/**
 * MyWorkLaneSkeleton — Loading-state scaffold for <MyWorkLane>.
 *
 * Mirrors the six-section layout of the full screen to prevent layout reflow
 * when the hook transitions loading → ready. Renders only placeholder shapes —
 * no text content, no fetched data.
 *
 * Sections:
 *   1. Hero (salutation + subtitle)
 *   2. Swimlane (3 columns × 3 card placeholders)
 *   3. In-flight runs (DataTable skeleton, 3 rows)
 *   4. Queue (DataTable skeleton, 5 rows)
 *   5. Decisions snippet (3 row placeholders)
 *   6. Recent contributions (DataTable skeleton, 3 rows)
 *
 * Uses the deployed <PageShell> and <SkeletonRow> primitives.
 * Plain divs with animate-pulse for non-row shapes (hero, swimlane cards).
 *
 * @see specs/prd.md §5.16
 */
import '../../styles/screens/today.css';
import React from "react";
import { PageShell } from "../../components/layout/PageShell";
import { SkeletonRow } from "../../components/primitives";

// ── Styles (inline — no CSS module needed for skeleton) ────────────────

const sectionStyle: React.CSSProperties = {
  marginBottom: "24px",
};

const heroLineStyle: React.CSSProperties = {
  height: "32px",
  width: "60%",
  backgroundColor: "rgba(0, 0, 0, 0.04)",
  borderRadius: "8px",
  marginBottom: "12px",
};

const heroSubtitleStyle: React.CSSProperties = {
  height: "20px",
  width: "40%",
  backgroundColor: "rgba(0, 0, 0, 0.04)",
  borderRadius: "8px",
};

const gridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr",
  gap: "16px",
};

const columnStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
};

const cardStyle: React.CSSProperties = {
  height: "80px",
  width: "100%",
  backgroundColor: "rgba(0, 0, 0, 0.04)",
  borderRadius: "8px",
};

const snippetRowStyle: React.CSSProperties = {
  height: "40px",
  width: "100%",
  backgroundColor: "rgba(0, 0, 0, 0.04)",
  borderRadius: "8px",
  marginBottom: "8px",
};

const sectionLabelStyle: React.CSSProperties = {
  height: "16px",
  width: "30%",
  backgroundColor: "rgba(0, 0, 0, 0.04)",
  borderRadius: "4px",
  marginBottom: "12px",
};

// ── Component ──────────────────────────────────────────────────────────

export default function MyWorkLaneSkeleton(): React.ReactElement {
  return (
    <PageShell>
      <div data-testid="my-work-lane-skeleton">
        {/* Hero: salutation + subtitle */}
        <div style={sectionStyle}>
          <div
            className="skeleton-product-display animate-pulse"
            style={heroLineStyle}
            aria-hidden="true"
          />
          <div
            className="skeleton-body-large animate-pulse"
            style={heroSubtitleStyle}
            aria-hidden="true"
          />
        </div>

        {/* Swimlane: 3 columns × 3 task-card placeholders */}
        <div style={sectionStyle}>
          <div
            className="skeleton-section-label animate-pulse"
            style={sectionLabelStyle}
            aria-hidden="true"
          />
          <div style={gridStyle}>
            {[0, 1, 2].map((col) => (
              <div key={col} style={columnStyle}>
                {[0, 1, 2].map((row) => (
                  <div
                    key={row}
                    className="skeleton-task-card animate-pulse"
                    style={cardStyle}
                    aria-hidden="true"
                  />
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* In-flight runs: DataTable skeleton with 3 row placeholders */}
        <div style={sectionStyle}>
          <div
            className="skeleton-section-label animate-pulse"
            style={sectionLabelStyle}
            aria-hidden="true"
          />
          <SkeletonRow count={3} />
        </div>

        {/* Queue: DataTable skeleton with 5 row placeholders */}
        <div style={sectionStyle}>
          <div
            className="skeleton-section-label animate-pulse"
            style={sectionLabelStyle}
            aria-hidden="true"
          />
          <SkeletonRow count={5} />
        </div>

        {/* Decisions snippet: 3 row placeholders */}
        <div style={sectionStyle}>
          <div
            className="skeleton-section-label animate-pulse"
            style={sectionLabelStyle}
            aria-hidden="true"
          />
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="skeleton-snippet-row animate-pulse"
              style={snippetRowStyle}
              aria-hidden="true"
            />
          ))}
        </div>

        {/* Recent contributions: DataTable skeleton with 3 row placeholders */}
        <div style={sectionStyle}>
          <div
            className="skeleton-section-label animate-pulse"
            style={sectionLabelStyle}
            aria-hidden="true"
          />
          <SkeletonRow count={3} />
        </div>
      </div>
    </PageShell>
  );
}
